import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useDebounce } from 'react-use';
import uuid from 'uuid';
import {
  CompanyNameFilter,
  CompanyMode,
  TalentMatchingFilters,
} from 'talent_matching/types';

export type ExperienceState = {
  minValue: string;
  maxValue: string;
  companyValues: Record<CompanyMode, string[]>;
  setMinValue: Dispatch<SetStateAction<string>>;
  setMaxValue: Dispatch<SetStateAction<string>>;
  handleCompaniesChange: (mode: CompanyMode, values: string[]) => void;
  handleRemoveCompany: (mode: CompanyMode, value: string) => void;
  clear: () => void;
  totalFiltersCount: number;
};

export const useExperienceState = (): ExperienceState => {
  const { setValue, watch } = useFormContext<TalentMatchingFilters>();
  const [minExperience, maxExperience, companyNameFilters] = watch([
    'minExperience',
    'maxExperience',
    'companyNameFilters',
  ]);
  const [minValue, setMinValue] = useState(minExperience || '');
  const [maxValue, setMaxValue] = useState(maxExperience || '');

  // Saved filters predating the chip UI carry blank rows; skipped here, dropped on next change.
  const companyValues = useMemo(() => {
    const values: Record<CompanyMode, string[]> = {
      can_have: [],
      include: [],
      exclude: [],
    };

    (companyNameFilters || []).forEach((filter) => {
      if (filter.value?.trim() && values[filter.mode]) {
        values[filter.mode].push(filter.value);
      }
    });

    return values;
  }, [companyNameFilters]);

  const handleCompaniesChange = (mode: CompanyMode, values: string[]) => {
    const currentRows = companyNameFilters || [];
    const otherModeRows = currentRows.filter((row) => row.mode !== mode);
    const modeRows: CompanyNameFilter[] = values
      .filter((value) => value.trim())
      .map(
        (value) =>
          currentRows.find(
            (row) => row.mode === mode && row.value === value
          ) || { id: uuid.v4(), mode, value }
      );

    setValue('companyNameFilters', [...otherModeRows, ...modeRows], {
      shouldDirty: true,
    });
  };

  const handleRemoveCompany = (mode: CompanyMode, value: string) => {
    handleCompaniesChange(
      mode,
      companyValues[mode].filter((v) => v !== value)
    );
  };

  const companyNameFiltersAppliedCount =
    companyValues.can_have.length +
    companyValues.include.length +
    companyValues.exclude.length;

  const totalFiltersCount =
    companyNameFiltersAppliedCount + (minExperience || maxExperience ? 1 : 0);

  const clear = () => {
    setMinValue('');
    setMaxValue('');
    setValue('minExperience', null, { shouldDirty: true });
    setValue('maxExperience', null, { shouldDirty: true });
    setValue('companyNameFilters', [], { shouldDirty: true });
  };

  useDebounce(
    () => {
      const normalizedMinValue = minValue === '' ? null : minValue;
      const normalizedMaxValue = maxValue === '' ? null : maxValue;

      if (minExperience !== normalizedMinValue) {
        setValue('minExperience', normalizedMinValue, { shouldDirty: true });
      }

      if (maxExperience !== normalizedMaxValue) {
        setValue('maxExperience', normalizedMaxValue, { shouldDirty: true });
      }
    },
    500,
    [minValue, maxValue, minExperience, maxExperience, setValue]
  );

  useEffect(() => {
    setMinValue(minExperience || '');
    setMaxValue(maxExperience || '');
  }, [minExperience, maxExperience]);

  return {
    minValue,
    maxValue,
    companyValues,
    setMinValue,
    setMaxValue,
    handleCompaniesChange,
    handleRemoveCompany,
    clear,
    totalFiltersCount,
  };
};
